interface LogoProps {
  className?: string
  showText?: boolean
  size?: 'sm' | 'md' | 'lg'
}

export default function Logo({ className = '', showText = true, size = 'md' }: LogoProps) {
  const sizeClasses = {
    sm: 'h-8',
    md: 'h-10',
    lg: 'h-14'
  }
  
  const textSizeClasses = {
    sm: 'text-lg',
    md: 'text-2xl',
    lg: 'text-3xl'
  }
  
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {/* Icon */}
      <svg
        className={sizeClasses[size]}
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="oceanGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#0ea5e9" />
            <stop offset="100%" stopColor="#0369a1" />
          </linearGradient>
          <linearGradient id="hatGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#1e293b" />
            <stop offset="100%" stopColor="#0f172a" />
          </linearGradient>
          <style>
            {`
              @keyframes wave {
                0%, 100% { transform: translateY(0) rotate(0deg); }
                50% { transform: translateY(-2px) rotate(5deg); }
              }
              @keyframes sail {
                0%, 100% { transform: translateX(0); }
                50% { transform: translateX(2px); }
              }
              @keyframes blink {
                0%, 90%, 100% { opacity: 1; }
                95% { opacity: 0; }
              }
              .pirate-wave { animation: wave 2s ease-in-out infinite; transform-origin: bottom center; }
              .sail-move { animation: sail 3s ease-in-out infinite; }
              .eye-blink { animation: blink 4s ease-in-out infinite; }
            `}
          </style>
        </defs>
        
        {/* Ocean wave background */}
        <circle cx="32" cy="32" r="28" fill="url(#oceanGrad)" opacity="0.15" />
        
        {/* Pirate character */}
        <g className="pirate-wave">
          {/* Head */}
          <circle cx="32" cy="30" r="11" fill="#fbbf24" />
          
          {/* Pirate hat */}
          <g fill="url(#hatGrad)">
            <ellipse cx="32" cy="22" rx="13" ry="4" />
            <path d="M 19 22 L 22 14 L 32 12 L 42 14 L 45 22 Z" />
            {/* Skull and crossbones on hat */}
            <circle cx="32" cy="17" r="2" fill="white" opacity="0.9" />
            <rect x="30.5" y="19" width="3" height="1" rx="0.5" fill="white" opacity="0.9" />
          </g>
          
          {/* Eye patch */}
          <rect x="35" y="28" width="5" height="3" rx="1" fill="#1e293b" />
          <line x1="37" y1="28" x2="32" y2="24" stroke="#1e293b" strokeWidth="0.5" />
          
          {/* Good eye */}
          <circle cx="28" cy="29" r="1.5" fill="white" className="eye-blink" />
          <circle cx="28" cy="29" r="0.8" fill="#1e293b" className="eye-blink" />
          
          {/* Smile */}
          <path d="M 26 34 Q 32 36 38 34" stroke="#1e293b" strokeWidth="1.5" fill="none" strokeLinecap="round" />
          
          {/* Beard */}
          <path d="M 24 36 Q 26 40 28 38 M 32 38 Q 34 40 36 38" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" />
        </g>
        
        {/* Ship wheel */}
        <g transform="translate(32, 50)" opacity="0.6">
          <circle r="5" fill="none" stroke="#0369a1" strokeWidth="1.5" />
          <line x1="0" y1="-5" x2="0" y2="5" stroke="#0369a1" strokeWidth="1" />
          <line x1="-5" y1="0" x2="5" y2="0" stroke="#0369a1" strokeWidth="1" />
        </g>
        
        {/* Sailing flag */}
        <g transform="translate(52, 18)" className="sail-move">
          <line x1="0" y1="0" x2="0" y2="12" stroke="#64748b" strokeWidth="1" />
          <path d="M 0 2 L 8 4 L 8 8 L 0 10 Z" fill="#ef4444" opacity="0.8" />
        </g>
      </svg>
      
      {/* Text */}
      {showText && (
        <div className="flex flex-col">
          <div className={`font-black ${textSizeClasses[size]} leading-none tracking-tight mb-0.5`}>
            <span className="text-sky-600">Captain</span>
            <span className="text-slate-900">CRM</span>
          </div>
          <span className="text-[10px] text-slate-400 font-medium tracking-wider uppercase">
            Ahoy Data Matey!
          </span>
        </div>
      )}
    </div>
  )
}
